import React, { VFC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileItem } from '@dxb-web-ui-framework/design-system';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { MimeType } from '../../../utils/mimeTypesToDownload';
import { downloadTransactionDetailsFile } from '../../../utils/fileHelpers';

export type TransferDocumentProps = {
  transferService: TransferServiceImpl;
  fileName: string;
  fileId: number;
  fileData: string;
  fileDescription: string;
  transactionId: number;
  onRefetchData: () => void;
  shouldHideDeleteButton?: boolean;
};

const TransferDoc: VFC<TransferDocumentProps> = ({
  transferService,
  fileName,
  fileId,
  fileData,
  fileDescription,
  transactionId,
  onRefetchData,
  shouldHideDeleteButton,
}) => {
  const { t } = useTranslation();
  const [timestamp, setTimestamp] = useState<Nullable<string>>(null);
  const [action, setAction] = useState<Nullable<'download' | 'delete'>>(null);
  const shouldDownloadDocument = action === 'download' && Boolean(timestamp);

  const shouldDeleteDocument = action === 'delete' && Boolean(timestamp);
  const { isLoading: isLoadingDeleteDocument } = useInvokeSWRService(
    transferService.DeleteDocument,
    {
      transactionId,
      fileId,
      fileName,
      fileDescription,
      fileData,
      nextCallback: onRefetchData,
    },
    shouldDeleteDocument,
    'transferService.DeleteDocument',
  );

  useEffect(() => {
    if (shouldDownloadDocument) {
      downloadTransactionDetailsFile(fileName, fileData, fileName.split('.')[1] as MimeType);
    }
  }, [fileData, fileName, shouldDownloadDocument]);

  const handleClickFile = (buttonAction: 'download' | 'delete') => {
    setTimestamp(null);
    setAction(buttonAction);
    setTimestamp(new Date().toISOString());
  };

  const actionButtons = [
    { type: 'download', onClick: () => handleClickFile('download'), title: t('FileItem.Download.Action') },
  ];

  if (actionButtons.length === 1 && !shouldHideDeleteButton) {
    actionButtons.push({ type: 'close', onClick: () => handleClickFile('delete'), title: t('FileItem.Delete.Action') });
  }

  const isLoading = isLoadingDeleteDocument;
  return <FileItem hasSkeleton={isLoading} key={fileId} filename={fileName} state={actionButtons} />;
};

export { TransferDoc };
